import React from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { AppModal } from '../../../components/AppModal';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../utils/settingsSectionStyles';

export type IdsSearchCompany = {
  id: number;
  server: 'open' | 'shk' | 'direct';
  name: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  customerNumberRequired: boolean;
  usernameRequired: boolean;
  passwordRequired: boolean;
};

const RADIUS_OPTIONS = [
  { value: '25', label: '25 km' },
  { value: '50', label: '50 km' },
  { value: '100', label: '100 km' },
];

function companyLocationKey(c: IdsSearchCompany): string {
  return `${c.server}|${c.id}|${c.postalCode}|${c.street}|${c.city}`;
}

function uniqueBranches(branches: IdsSearchCompany[]): IdsSearchCompany[] {
  const seen = new Set<string>();
  return branches.filter((c) => {
    const key = `${c.postalCode}|${c.street.trim().toLowerCase()}|${c.city.trim().toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function groupCompanies(companies: IdsSearchCompany[]): Array<{ key: string; name: string; branches: IdsSearchCompany[] }> {
  const order: string[] = [];
  const byKey = new Map<string, IdsSearchCompany[]>();
  for (const c of companies) {
    const key = `${c.server}:${c.id}`;
    const list = byKey.get(key);
    if (list) list.push(c);
    else {
      byKey.set(key, [c]);
      order.push(key);
    }
  }
  return order.map((key) => {
    const branches = uniqueBranches(byKey.get(key) ?? []);
    return { key, name: branches[0]?.name ?? '', branches };
  });
}

export function formatIdsAddress(c: { street?: string; postalCode?: string; city?: string }): string {
  return [c.street, [c.postalCode, c.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

function CompanyGroup({
  name,
  branches,
  selectedKey,
  onSelect,
}: {
  name: string;
  branches: IdsSearchCompany[];
  selectedKey: string | null;
  onSelect: (c: IdsSearchCompany) => void;
}) {
  return (
    <li className="px-3 py-2">
      <p className="font-medium text-app-text text-sm">{name}</p>
      <ul className="mt-1 space-y-0.5">
        {branches.map((c, idx) => {
          const key = companyLocationKey(c);
          const selected = selectedKey === key;
          return (
            <li key={`${key}-${idx}`}>
              <button
                type="button"
                className={`w-full text-left rounded-md px-2 py-1.5 text-xs ${
                  selected
                    ? 'bg-blue-50 text-app-text ring-1 ring-pipedrive-blue/30'
                    : 'text-app-text-muted hover:bg-app-hover'
                }`}
                onClick={() => onSelect(c)}
              >
                {formatIdsAddress(c) || 'Adresse unbekannt'}
              </button>
            </li>
          );
        })}
      </ul>
    </li>
  );
}

function RequiredMark() {
  return <span className="text-red-500"> *</span>;
}

function DirectShopRows({
  shops,
  selectedKey,
  onSelect,
}: {
  shops: Array<{ id: number; name: string }>;
  selectedKey: string | null;
  onSelect: (c: IdsSearchCompany) => void;
}) {
  if (shops.length === 0) return null;
  return (
    <div className="shrink-0 pt-3 mt-auto border-t border-app-border">
      <p className="text-[11px] font-medium text-slate-400 mb-1.5">Weitere Großhändler</p>
      <ul className="divide-y divide-app-border border border-app-border rounded-md">
        {shops.map((shop) => {
          const company = companyFromDirectShop(shop);
          const selected = selectedKey === companyLocationKey(company);
          return (
            <li key={`direct-${shop.id}`}>
              <button
                type="button"
                className={`w-full text-left px-3 py-2 rounded-md ${
                  selected
                    ? 'bg-blue-50 text-app-text ring-1 ring-inset ring-pipedrive-blue/30'
                    : 'hover:bg-app-hover'
                }`}
                onClick={() => onSelect(company)}
              >
                <span className="block font-medium text-app-text text-sm">{shop.name}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function companyFromDirectShop(shop: { id: number; name: string }): IdsSearchCompany {
  return {
    id: shop.id,
    server: 'direct',
    name: shop.name,
    street: '',
    postalCode: '',
    city: '',
    country: '',
    customerNumberRequired: false,
    usernameRequired: true,
    passwordRequired: true,
  };
}

export type IdsEditConnection = {
  id: string;
  server: 'shk' | 'open' | 'direct';
  companyId: number;
  companyName: string;
  street: string;
  postalCode: string;
  city: string;
  customerNumberRequired: boolean;
  usernameRequired: boolean;
  passwordRequired: boolean;
  customerNumber: string;
  username: string;
  hasPassword: boolean;
};

function companyFromEdit(c: IdsEditConnection): IdsSearchCompany {
  return {
    id: c.companyId,
    server: c.server,
    name: c.companyName,
    street: c.street,
    postalCode: c.postalCode,
    city: c.city,
    country: '',
    customerNumberRequired: c.customerNumberRequired,
    usernameRequired: c.usernameRequired,
    passwordRequired: c.passwordRequired,
  };
}

type IdsAddConnectionModalProps = {
  open: boolean;
  defaultPostalCode: string;
  directShops: Array<{ id: number; name: string; catalog?: 'omd' }>;
  connectedDirectIds: number[];
  editConnection: IdsEditConnection | null;
  onClose: () => void;
  onSaved: (result: { testedOk: boolean; testError?: string }) => void;
};

export function IdsAddConnectionModal({
  open,
  defaultPostalCode,
  directShops,
  connectedDirectIds,
  editConnection,
  onClose,
  onSaved,
}: IdsAddConnectionModalProps) {
  const [postalCode, setPostalCode] = React.useState(defaultPostalCode);
  const [radiusKm, setRadiusKm] = React.useState('50');
  const [nameFilter, setNameFilter] = React.useState('');
  const [searching, setSearching] = React.useState(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [companies, setCompanies] = React.useState<IdsSearchCompany[]>([]);
  const [selectedCompany, setSelectedCompany] = React.useState<IdsSearchCompany | null>(null);
  const [customerNumber, setCustomerNumber] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const plzRef = React.useRef<HTMLInputElement>(null);

  const isEdit = Boolean(editConnection);
  const hasStoredPassword = Boolean(editConnection?.hasPassword);

  const reset = React.useCallback(() => {
    setPostalCode(defaultPostalCode);
    setRadiusKm('50');
    setNameFilter('');
    setSearching(false);
    setHasSearched(false);
    setCompanies([]);
    setSelectedCompany(editConnection ? companyFromEdit(editConnection) : null);
    setCustomerNumber(editConnection?.customerNumber ?? '');
    setUsername(editConnection?.username ?? '');
    setPassword('');
    setPasswordVisible(false);
    setSaving(false);
    setError(null);
  }, [defaultPostalCode, editConnection]);

  React.useEffect(() => {
    if (!open) return;
    reset();
    if (editConnection) return;
    const t = window.setTimeout(() => plzRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open, reset, editConnection]);

  useEscapeClose(onClose, open && !saving);

  const filteredCompanies = React.useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => {
      const hay = `${c.name} ${c.city} ${c.postalCode} ${c.street}`.toLowerCase();
      return hay.includes(q);
    });
  }, [companies, nameFilter]);

  const grouped = React.useMemo(() => groupCompanies(filteredCompanies), [filteredCompanies]);
  const selectedKey = selectedCompany ? companyLocationKey(selectedCompany) : null;
  const availableDirectShops = React.useMemo(
    () => directShops.filter((shop) => !connectedDirectIds.includes(shop.id)),
    [directShops, connectedDirectIds],
  );

  const selectCompany = (c: IdsSearchCompany) => {
    setSelectedCompany(c);
    if (selectedKey !== companyLocationKey(c)) {
      setCustomerNumber('');
      setUsername('');
      setPassword('');
      setPasswordVisible(false);
      setError(null);
    }
  };

  const search = async () => {
    setSearching(true);
    setError(null);
    setHasSearched(true);
    try {
      const res = await fetch(
        `/api/settings/ids/search?postalCode=${encodeURIComponent(postalCode)}&radiusKm=${encodeURIComponent(radiusKm)}&process=SHA`,
        { credentials: 'include' },
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || 'Suche fehlgeschlagen.');
      setCompanies(
        (Array.isArray(d.companies) ? d.companies : []).map((c: IdsSearchCompany) => ({
          ...c,
          server: c.server === 'shk' ? 'shk' : 'open',
        })),
      );
      setSelectedCompany(null);
    } catch (err) {
      setCompanies([]);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSearching(false);
    }
  };

  const saveConnection = async (company: IdsSearchCompany) => {
    if (company.customerNumberRequired && !customerNumber.trim()) {
      setError('Bitte die Kundennummer eintragen.');
      return;
    }
    if (company.usernameRequired && !username.trim()) {
      setError('Bitte den Benutzernamen eintragen.');
      return;
    }
    if (company.passwordRequired && !password.trim() && !hasStoredPassword) {
      setError('Bitte das Passwort eintragen.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/settings/ids/connections', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          server: company.server === 'shk' ? 'shk' : company.server === 'direct' ? 'direct' : 'open',
          companyId: company.id,
          companyName: company.name,
          street: company.street,
          postalCode: company.postalCode,
          city: company.city,
          country: company.country,
          customerNumberRequired: company.customerNumberRequired,
          usernameRequired: company.usernameRequired,
          passwordRequired: company.passwordRequired,
          customerNumber,
          username,
          password,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.error || 'Speichern fehlgeschlagen.');
      const savedId = String(d?.connection?.id ?? '');
      if (!savedId) {
        onSaved({ testedOk: true });
        return;
      }
      const testRes = await fetch(`/api/settings/ids/connections/${encodeURIComponent(savedId)}/test`, {
        method: 'POST',
        credentials: 'include',
      });
      const td = await testRes.json().catch(() => ({}));
      if (!testRes.ok) {
        onSaved({ testedOk: false, testError: td?.error || 'Prüfung der Shop-Anbindung fehlgeschlagen.' });
        return;
      }
      onSaved({ testedOk: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      closeOnOverlayClick={false}
      disableClose={saving}
      overlayClassName="z-50"
      panelClassName={isEdit ? 'md:max-w-lg md:h-auto' : 'md:max-w-5xl md:h-[min(42rem,90vh)]'}
      ariaLabelledBy="ids-add-connection-title"
    >
      <div className="flex flex-col flex-1 min-h-0 h-full">
        <div className="px-5 py-3 border-b border-app-border flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 id="ids-add-connection-title" className="text-sm font-semibold text-app-text">
              {isEdit ? 'Shop-Zugang bearbeiten' : 'Großhändler hinzufügen'}
            </h2>
            <p className="text-xs text-app-text-subtle mt-0.5">
              {isEdit
                ? 'Zugangsdaten ändern und erneut prüfen.'
                : 'Per PLZ suchen, Niederlassung wählen, Shop-Zugang eintragen.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-app-border text-app-text-subtle hover:bg-app-hover hover:text-app-text disabled:opacity-40"
            aria-label="Schließen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error ? (
          <div className="mx-5 mt-3 p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200 shrink-0">
            {error}
          </div>
        ) : null}

        {!isEdit ? (
        <div className="px-5 py-3 border-b border-app-border shrink-0">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[8rem]">
              <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>PLZ</label>
              <input
                ref={plzRef}
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className={SETTINGS_INPUT_CLASS}
                inputMode="numeric"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void search();
                  }
                }}
              />
            </div>
            <div className="w-32">
              <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>Umkreis</label>
              <AppSelectDropdown
                value={radiusKm}
                onChange={setRadiusKm}
                options={RADIUS_OPTIONS}
                className="w-full"
                triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
              />
            </div>
            <button
              type="button"
              className={SETTINGS_BTN_OUTLINE}
              onClick={() => void search()}
              disabled={searching || saving || !postalCode.trim()}
            >
              {searching ? <LoadingSpinner size="sm" /> : null}
              Suchen
            </button>
          </div>
        </div>
        ) : null}

        <div className={isEdit ? 'p-5' : 'flex-1 min-h-0 overflow-y-auto lg:overflow-hidden lg:grid lg:grid-cols-[minmax(0,1fr)_22rem]'}>
          {!isEdit ? (
          <div className="flex flex-col min-h-0 lg:border-r lg:border-app-border p-5 gap-2">
            {hasSearched && companies.length > 0 ? (
              <div>
                <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>In den Treffern filtern</label>
                <input
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  className={SETTINGS_INPUT_CLASS}
                  placeholder="Name, Ort oder PLZ"
                />
              </div>
            ) : null}

            {searching ? <InlineLoadingState label="Großhändler werden gesucht …" className="justify-start" /> : null}

            {!searching && !hasSearched ? (
              <p className="text-xs text-app-text-subtle">PLZ eingeben und suchen.</p>
            ) : null}

            {!searching && hasSearched && !error && companies.length === 0 ? (
              <p className="text-xs text-app-text-subtle">Keine Großhändler in diesem Umkreis.</p>
            ) : null}

            {!searching && companies.length > 0 ? (
              <>
                <p className="text-xs text-app-text-subtle">
                  {filteredCompanies.length} von {companies.length} Treffern, nächste zur PLZ zuerst
                </p>
                {filteredCompanies.length === 0 ? (
                  <p className="text-xs text-app-text-subtle">Keine Treffer für „{nameFilter.trim()}“ in dieser Liste.</p>
                ) : (
                  <ul className="divide-y divide-app-border border border-app-border rounded-md flex-1 min-h-0 overflow-y-auto">
                    {grouped.map((group) => (
                      <CompanyGroup
                        key={group.key}
                        name={group.name}
                        branches={group.branches}
                        selectedKey={selectedKey}
                        onSelect={selectCompany}
                      />
                    ))}
                  </ul>
                )}
              </>
            ) : null}

            <DirectShopRows shops={availableDirectShops} selectedKey={selectedKey} onSelect={selectCompany} />
          </div>
          ) : null}

          <div className={isEdit ? '' : 'p-5 lg:overflow-y-auto'}>
            {selectedCompany ? (
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-app-text">{selectedCompany.name}</p>
                  <p className="text-xs text-app-text-subtle mt-0.5">
                    {selectedCompany.server === 'direct'
                      ? 'Shop-Zugang eintragen. Die Shop-Adresse setzt Meisterpfad.'
                      : formatIdsAddress(selectedCompany) || 'Adresse unbekannt'}
                  </p>
                </div>
                <div>
                  <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
                    Kundennummer
                    {selectedCompany.customerNumberRequired ? <RequiredMark /> : null}
                  </label>
                  <input
                    value={customerNumber}
                    onChange={(e) => setCustomerNumber(e.target.value)}
                    className={SETTINGS_INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
                    Benutzername
                    {selectedCompany.usernameRequired ? <RequiredMark /> : null}
                  </label>
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className={SETTINGS_INPUT_CLASS}
                    autoComplete="username"
                  />
                </div>
                <div>
                  <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
                    Passwort
                    {selectedCompany.passwordRequired && !hasStoredPassword ? <RequiredMark /> : null}
                  </label>
                  <div className="relative">
                    <input
                      type={passwordVisible ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${SETTINGS_INPUT_CLASS} pr-10`}
                      autoComplete="new-password"
                      placeholder={hasStoredPassword ? 'Unverändert lassen' : undefined}
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordVisible((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-app-text-muted hover:bg-app-hover"
                      aria-label={passwordVisible ? 'Passwort verbergen' : 'Passwort anzeigen'}
                      title={passwordVisible ? 'Passwort verbergen' : 'Passwort anzeigen'}
                    >
                      {passwordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  className={SETTINGS_BTN_PRIMARY}
                  disabled={saving}
                  onClick={() => void saveConnection(selectedCompany)}
                >
                  {saving ? <LoadingSpinner size="sm" /> : null}
                  Speichern und prüfen
                </button>
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-app-border p-4 text-xs text-app-text-subtle">
                Niederlassung in der Liste wählen, dann hier den Shop-Zugang eintragen.
              </div>
            )}
          </div>
        </div>
      </div>
    </AppModal>
  );
}
